// Cria a Pessoa Jurídica da sociedade nos pedidos de registro (Unipessoal 194 /
// Pluripessoal 322), via POST /pessoa/juridica da API de Cadastro/Login — o ID
// devolvido vai no `nomeSociedade` do /criar da API de Protocolo (obrigatório
// pra esses 2 modelos). Nesse momento a sociedade ainda não tem CNPJ, então a
// API grava só em Pessoa, sem login.
//
// Só o que é da sociedade vai pra PJ: nome, capital social, subseção e o
// Endereço Profissional da Sociedade. O Endereço Residencial do Advogado e a
// Quantidade de Sócios ficam de fora (são do boleto, ver pagadorResolverService
// e paymentConfig.quantityPricing).
//
// Idempotente do lado da API: se já existir uma PJ em aberto do mesmo usuário
// com esse nome (tentativa anterior que não foi até o fim), ela é regravada com
// os dados atuais e volta o mesmo pessoaId (200, reaproveitada: true) — por
// isso dá pra chamar a cada tentativa, sem guardar o ID no estado da página.
import { pessoaService } from '../../../user/services/pessoaService';
import { resolverEnderecoPorCep } from './enderecoResolverService';
import { criteriosCepDoFormulario } from '../../../address/utils/cepRegistros';

const MENSAGENS_ERRO = {
  NOME_ALREADY_EXISTS: 'Já existe uma sociedade cadastrada com este nome. Confira o Nome da Sociedade ou entre em contato com a OAB/MS.',
  INVALID_PHONE: 'O celular do Endereço Profissional da Sociedade é inválido. Informe DDD + número.',
  INVALID_EMAIL: 'O e-mail do Endereço Profissional da Sociedade é inválido.',
  INVALID_VALOR_CAPITAL_SOCIAL: 'O Valor do Capital Social é inválido. Informe um valor maior que zero.'
};

const MENSAGEM_PADRAO = 'Não foi possível cadastrar a sociedade. Tente novamente em instantes.';

/**
 * Monta o body do POST /pessoa/juridica a partir do formData do pedido e dos
 * IDs de endereço já resolvidos do CEP Profissional. Função pura.
 *
 * Telefones: o obrigatório do formulário é o celular, e o da API é
 * telefoneComercial — por isso celular -> telefoneComercial e o telefone fixo
 * (opcional) -> telefone2Comercial.
 */
export const montarPayloadPessoaJuridica = (formData, endereco) => ({
  nome: String(formData.nomeSociedade || '').trim(),

  emailComercial: formData.emailProfissional,
  telefoneComercial: formData.celularProfissional,
  ...(formData.telefoneProfissional && { telefone2Comercial: formData.telefoneProfissional }),

  cepComercial: Number(endereco.cepId),
  paisComercial: Number(endereco.paisId),
  estadoComercial: Number(endereco.estadoId),
  municipioComercial: Number(endereco.municipioId),
  bairroComercial: formData.bairroProfissional,
  numeroComercial: formData.numeroProfissional,
  logradouroComercial: formData.ruaProfissional,
  ...(formData.complementoProfissional?.trim() && { complementoComercial: formData.complementoProfissional.trim() }),

  // O formulário só oferece o Endereço Profissional como correspondência.
  correspondenciaComercial: 'S',

  valorCapitalSocial: formData.valorCapitalSocial,
  subUnidadeAtual: Number(formData.subsecaoSociedade)
});

const mensagemDoErro = (error) => {
  const { code, message, missingFields } = error?.response?.data || {};
  if (MENSAGENS_ERRO[code]) return MENSAGENS_ERRO[code];
  if (code === 'MISSING_FIELDS' && missingFields?.length) {
    return `Não foi possível cadastrar a sociedade: faltam os campos ${missingFields.join(', ')}.`;
  }
  return message || MENSAGEM_PADRAO;
};

/**
 * Cria (ou reaproveita) a PJ da sociedade e devolve o pessoaId dela. Lança
 * Error com mensagem pronta pra tela em qualquer falha — sem esse ID o /criar
 * recusa o pedido de qualquer jeito, então não há o que seguir sem ele.
 */
export async function criarPessoaJuridicaSociedade(formData) {
  const endereco = await resolverEnderecoPorCep(formData.cepProfissional, criteriosCepDoFormulario(formData, 'cepProfissional'));
  if (!endereco?.cepId || !endereco?.paisId || !endereco?.estadoId || !endereco?.municipioId) {
    throw new Error('Não foi possível validar o CEP do Endereço Profissional da Sociedade. Confira o CEP e tente novamente.');
  }

  let response;
  try {
    response = await pessoaService.criarPessoaJuridica(montarPayloadPessoaJuridica(formData, endereco));
  } catch (error) {
    throw new Error(mensagemDoErro(error));
  }

  const pessoaId = response?.data?.pessoaId;
  if (!pessoaId) {
    throw new Error(MENSAGEM_PADRAO);
  }
  return Number(pessoaId);
}
